import React from 'react';
import { Search, Bell, Moon, Sun, Download, Upload, Settings as SettingsIcon } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import clsx from 'clsx';

export const Header: React.FC = () => {
  const { 
    ui: { theme, activeTab }, 
    toggleTheme 
  } = useAppStore();

  const getTabTitle = () => {
    switch (activeTab) {
      case 'config':
        return '配置识别';
      case 'providers':
        return '供应商管理';
      case 'chat':
        return '对话测试';
      default:
        return 'API 对话管理系统';
    }
  };

  const getTabDescription = () => {
    switch (activeTab) {
      case 'config':
        return '自动识别并解析 API 配置信息';
      case 'providers':
        return '管理您的 API 供应商和配置';
      case 'chat':
        return '测试 API 对话功能和性能';
      default:
        return '高效管理您的 AI API 调用';
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between px-6 py-4">
        {/* 左侧：标题和描述 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{getTabTitle()}</h1>
              <p className="text-sm text-gray-500 mt-0.5">{getTabDescription()}</p>
            </div>
            
            {/* 标签指示器 */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <div className={clsx(
                  'w-2 h-2 rounded-full',
                  activeTab === 'config' && 'bg-primary-500',
                  activeTab === 'providers' && 'bg-accent-500',
                  activeTab === 'chat' && 'bg-green-500'
                )} />
                <span className="text-xs font-medium text-gray-600">
                  {activeTab === 'config' && '识别中'}
                  {activeTab === 'providers' && '管理中'}
                  {activeTab === 'chat' && '测试中'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 右侧：操作按钮 */}
        <div className="flex items-center gap-3">
          {/* 搜索框 */}
          <div className="relative hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜索配置、供应商或对话..."
              className="pl-10 pr-4 py-2 w-64 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>

          {/* 导入导出按钮 */}
          <div className="flex items-center gap-1">
            <button
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="导入配置"
            >
              <Upload className="w-5 h-5 text-gray-600" />
            </button>
            <button
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="导出配置"
            >
              <Download className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* 分隔线 */}
          <div className="w-px h-6 bg-gray-300" />

          {/* 通知按钮 */}
          <button
            className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="通知"
          >
            <Bell className="w-5 h-5 text-gray-600" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>

          {/* 主题切换 */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title={theme === 'light' ? '切换深色模式' : '切换浅色模式'}
          >
            {theme === 'light' ? (
              <Moon className="w-5 h-5 text-gray-600" />
            ) : (
              <Sun className="w-5 h-5 text-gray-600" />
            )}
          </button>

          {/* 设置按钮 */}
          <button
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="设置"
          >
            <SettingsIcon className="w-5 h-5 text-gray-600" />
          </button>

          {/* 用户头像 */}
          <div className="relative group">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center cursor-pointer">
              <span className="text-white font-medium text-sm">U</span>
            </div>
            
            {/* 用户菜单 */}
            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="font-medium text-gray-900">用户账户</p>
                <p className="text-sm text-gray-500 mt-0.5">个人使用</p>
              </div>
              <div className="py-1">
                <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">
                  个人资料
                </button>
                <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">
                  使用统计
                </button>
                <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">
                  帮助与支持
                </button>
                <div className="my-1 border-t border-gray-100" />
                <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                  退出登录
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 进度指示器 */}
      <div className="px-6 pb-2">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-4">
            <div className={clsx(
              'flex items-center gap-1.5',
              activeTab === 'config' && 'text-primary-600 font-medium'
            )}>
              <div className={clsx(
                'w-2 h-2 rounded-full',
                activeTab === 'config' ? 'bg-primary-500' : 'bg-gray-300'
              )} />
              <span>配置识别</span>
            </div>
            <div className={clsx(
              'flex items-center gap-1.5',
              activeTab === 'providers' && 'text-accent-600 font-medium'
            )}>
              <div className={clsx(
                'w-2 h-2 rounded-full',
                activeTab === 'providers' ? 'bg-accent-500' : 'bg-gray-300'
              )} />
              <span>供应商管理</span>
            </div>
            <div className={clsx(
              'flex items-center gap-1.5',
              activeTab === 'chat' && 'text-green-600 font-medium'
            )}>
              <div className={clsx(
                'w-2 h-2 rounded-full',
                activeTab === 'chat' ? 'bg-green-500' : 'bg-gray-300'
              )} />
              <span>对话测试</span>
            </div>
          </div>
          
          <div className="text-gray-400">
            系统状态：<span className="text-green-600 font-medium">正常</span>
          </div>
        </div>
      </div>
    </header>
  );
};